import { useEffect, useState, useRef, useCallback } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { Bell, Check, CheckCircle, Warning, Info, X } from "@phosphor-icons/react";
import { ScrollArea } from "../components/ui/scroll-area";

const typeIcons = {
  success: { icon: CheckCircle, color: "#10B981" },
  warning: { icon: Warning, color: "#F59E0B" },
  info: { icon: Info, color: "#00E5FF" },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function NotificationItem({ n, onMarkRead }) {
  const TypeConfig = typeIcons[n.type] || typeIcons.info;
  const IconComp = TypeConfig.icon;
  return (
    <div className={`px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors flex gap-3 ${!n.read ? "bg-white/[0.02]" : ""}`}>
      <div className="mt-0.5">
        <IconComp size={16} weight="duotone" style={{ color: TypeConfig.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-xs ${!n.read ? "text-white font-medium" : "text-[#A1A1AA]"}`}>{n.title}</p>
          {!n.read && (
            <button onClick={() => onMarkRead(n.notification_id)} className="text-[#A1A1AA] hover:text-[#00E5FF] shrink-0" title="Mark as read">
              <Check size={12} />
            </button>
          )}
        </div>
        <p className="text-[10px] text-[#A1A1AA]/70 mt-0.5 line-clamp-2">{n.message}</p>
        <p className="text-[9px] text-[#A1A1AA]/50 mt-1">{timeAgo(n.created_at)}</p>
      </div>
    </div>
  );
}

function NotificationList({ notifications, onMarkRead }) {
  if (notifications.length === 0) {
    return <div className="py-8 text-center"><p className="text-xs text-[#A1A1AA]">No notifications yet</p></div>;
  }
  return notifications.map((n) => (
    <NotificationItem key={n.notification_id} n={n} onMarkRead={onMarkRead} />
  ));
}

export default function NotificationPanel() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const ws = useWebSocket();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`, { headers: getAuthHeaders() });
      setNotifications(res.data.items || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      logger.error("Notifications error:", err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!ws) return;
    const unsub = ws.subscribe("notification_update", fetchNotifications);
    return unsub;
  }, [ws, fetchNotifications]);

  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  }, []);

  const prevUnread = useRef(0);
  useEffect(() => {
    if (unreadCount > prevUnread.current && prevUnread.current > 0) {
      if ("Notification" in window && Notification.permission === "granted") {
        const latest = notifications.find((n) => !n.read);
        if (latest) new Notification("VeloxAlgoTech LLP Admin Alert", { body: latest.message });
      }
    }
    prevUnread.current = unreadCount;
  }, [unreadCount, notifications]);

  const markRead = useCallback(async (id) => {
    await axios.post(`${API}/admin/notifications/${id}/read`, {}, { headers: getAuthHeaders() });
    fetchNotifications();
  }, [fetchNotifications]);

  const markAllRead = useCallback(async () => {
    await axios.post(`${API}/admin/notifications/read-all`, {}, { headers: getAuthHeaders() });
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="relative" ref={panelRef}>
      <button data-testid="notifications-button" onClick={() => setOpen(!open)} className="relative p-2 rounded-md hover:bg-white/5 transition-colors">
        <Bell size={18} className="text-[#A1A1AA]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#00E5FF] text-[9px] font-bold text-black flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-md border border-white/10 shadow-xl z-50" style={{ background: "#1A1A1A" }} data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h4 className="text-xs font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Notifications</h4>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && <button data-testid="mark-all-read" onClick={markAllRead} className="text-[10px] text-[#00E5FF] hover:underline">Mark all read</button>}
              <button onClick={() => setOpen(false)} className="text-[#A1A1AA] hover:text-white"><X size={14} /></button>
            </div>
          </div>
          <ScrollArea className="max-h-[360px]">
            <NotificationList notifications={notifications} onMarkRead={markRead} />
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
